export const DEFAULT_ITINERARY_PDF_THEME = {
    fontFamily: "Helvetica",
    colors: {
        orange: {
            50: "#fef4ee",
            100: "#fde6d7",
            200: "#fac9ae",
            300: "#f7a27a",
            400: "#f3764a",
            500: "#ef4e20",
            600: "#e03516",
        },
        black: {
            50: "#f6f6f6",
            100: "#e7e7e7",
            200: "#d1d1d1",
            300: "#b0b0b0",
            400: "#888888",
            500: "#6d6d6d",
            600: "#5d5d5d",
            700: "#4f4f4f",
            800: "#454545",
            900: "#333333",
            950: "#262626",
        },
        blue: {
            500: "#00c4cb",
            600: "#03a8b7",
            700: "#0a8594",
        },
        green: {
            500: "#33b436",
            600: "#259427",
        },
        white: "#ffffff",
    },
};
export function createItineraryPdfTheme(overrides = {}) {
    return {
        fontFamily: overrides.fontFamily ?? DEFAULT_ITINERARY_PDF_THEME.fontFamily,
        colors: {
            orange: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.orange,
                ...overrides.colors?.orange,
            },
            black: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.black,
                ...overrides.colors?.black,
            },
            blue: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.blue,
                ...overrides.colors?.blue,
            },
            green: {
                ...DEFAULT_ITINERARY_PDF_THEME.colors.green,
                ...overrides.colors?.green,
            },
            white: overrides.colors?.white ?? DEFAULT_ITINERARY_PDF_THEME.colors.white,
        },
    };
}
export function createItineraryPdfStyles(overrides = {}) {
    const theme = createItineraryPdfTheme(overrides);
    const { colors } = theme;
    const spacing = {
        xs: 4,
        sm: 8,
        md: 12,
        lg: 16,
        xl: 24,
        xxl: 32,
        xxxl: 48,
    };
    const typography = {
        label: {
            fontFamily: theme.fontFamily,
            fontSize: 8,
            fontWeight: "bold",
            color: colors.black[400],
            textTransform: "uppercase",
            letterSpacing: 0.5,
        },
        body: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            fontWeight: "normal",
            color: colors.black[600],
            lineHeight: 1.5,
        },
        bodySmall: {
            fontFamily: theme.fontFamily,
            fontSize: 9,
            fontWeight: "normal",
            color: colors.black[500],
            lineHeight: 1.4,
        },
        h3: {
            fontFamily: theme.fontFamily,
            fontSize: 14,
            fontWeight: "bold",
            color: colors.black[700],
        },
        price: {
            fontFamily: theme.fontFamily,
            fontSize: 14,
            fontWeight: "bold",
            color: colors.black[900],
        },
    };
    const coverStyles = {
        page: {
            fontFamily: theme.fontFamily,
            backgroundColor: colors.white,
        },
        heroSection: {
            backgroundColor: colors.black[950],
            paddingHorizontal: 40,
            paddingTop: 50,
            paddingBottom: 40,
            position: "relative",
        },
        accentBar: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: colors.orange[400],
        },
        logoContainer: {
            flexDirection: "row",
            alignItems: "center",
            marginBottom: spacing.xl,
        },
        logo: {
            width: 80,
            height: 80,
            objectFit: "contain",
            borderRadius: 8,
            backgroundColor: colors.white,
        },
        companyInfo: {
            marginLeft: spacing.lg,
            flex: 1,
        },
        companyName: {
            fontFamily: theme.fontFamily,
            fontSize: 22,
            fontWeight: "bold",
            color: colors.white,
            marginBottom: spacing.xs,
        },
        companyContact: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.black[300],
            marginBottom: 2,
        },
        heroTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 36,
            fontWeight: "bold",
            color: colors.white,
            letterSpacing: -1,
            marginTop: spacing.lg,
        },
        heroSubtitle: {
            fontFamily: theme.fontFamily,
            fontSize: 14,
            color: colors.black[300],
            marginTop: spacing.sm,
        },
        contentSection: {
            padding: 40,
            flex: 1,
        },
        customerCard: {
            backgroundColor: colors.black[50],
            borderRadius: 10,
            padding: spacing.xl,
            marginBottom: spacing.xl,
            borderLeftWidth: 4,
            borderLeftColor: colors.orange[400],
        },
        sectionTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 12,
            fontWeight: "bold",
            color: colors.black[400],
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: spacing.md,
        },
        customerName: {
            fontFamily: theme.fontFamily,
            fontSize: 20,
            fontWeight: "bold",
            color: colors.black[900],
            marginBottom: spacing.md,
        },
        customerDetails: {
            flexDirection: "row",
            flexWrap: "wrap",
        },
        customerDetailItem: {
            width: "50%",
            marginBottom: spacing.sm,
        },
        detailLabel: {
            ...typography.label,
            marginBottom: 2,
        },
        detailValue: {
            fontFamily: theme.fontFamily,
            fontSize: 11,
            color: colors.black[700],
        },
        tripSummarySection: {
            marginBottom: spacing.xl,
        },
        tripSummaryHeader: {
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: spacing.lg,
            paddingBottom: spacing.md,
            borderBottomWidth: 1,
            borderBottomColor: colors.black[100],
        },
        tripSummaryTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 14,
            fontWeight: "bold",
            color: colors.black[900],
        },
        serviceCount: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.black[400],
            backgroundColor: colors.black[100],
            paddingHorizontal: spacing.sm,
            paddingVertical: spacing.xs,
            borderRadius: 4,
        },
        serviceSummaryItem: {
            flexDirection: "row",
            marginBottom: spacing.md,
            paddingBottom: spacing.md,
            borderBottomWidth: 1,
            borderBottomColor: colors.black[50],
        },
        serviceIndex: {
            width: 24,
            height: 24,
            borderRadius: 12,
            backgroundColor: colors.orange[100],
            justifyContent: "center",
            alignItems: "center",
            marginRight: spacing.md,
        },
        serviceIndexText: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            fontWeight: "bold",
            color: colors.orange[600],
        },
        serviceSummaryContent: {
            flex: 1,
        },
        serviceSummaryName: {
            fontFamily: theme.fontFamily,
            fontSize: 12,
            fontWeight: "bold",
            color: colors.black[800],
            marginBottom: 2,
        },
        serviceSummaryDetails: {
            fontFamily: theme.fontFamily,
            fontSize: 9,
            color: colors.black[500],
        },
        serviceSummaryPrice: {
            fontFamily: theme.fontFamily,
            fontSize: 11,
            fontWeight: "bold",
            color: colors.black[900],
            textAlign: "right",
        },
        footer: {
            position: "absolute",
            bottom: 30,
            left: 40,
            right: 40,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: spacing.md,
            borderTopWidth: 1,
            borderTopColor: colors.black[100],
        },
        footerBranding: {
            fontFamily: theme.fontFamily,
            fontSize: 8,
            color: colors.black[300],
        },
        footerDate: {
            fontFamily: theme.fontFamily,
            fontSize: 8,
            color: colors.black[400],
        },
    };
    const serviceStyles = {
        page: {
            fontFamily: theme.fontFamily,
            backgroundColor: colors.white,
            paddingTop: 40,
            paddingBottom: 70,
            paddingHorizontal: 40,
        },
        header: {
            flexDirection: "row",
            alignItems: "center",
            marginBottom: spacing.xl,
            paddingBottom: spacing.lg,
            borderBottomWidth: 2,
            borderBottomColor: colors.orange[400],
        },
        serviceNumber: {
            width: 36,
            height: 36,
            borderRadius: 18,
            backgroundColor: colors.orange[400],
            justifyContent: "center",
            alignItems: "center",
            marginRight: spacing.lg,
        },
        serviceNumberText: {
            fontFamily: theme.fontFamily,
            fontSize: 16,
            fontWeight: "bold",
            color: colors.white,
        },
        headerContent: {
            flex: 1,
        },
        serviceTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 20,
            fontWeight: "bold",
            color: colors.black[900],
            marginBottom: spacing.xs,
        },
        providerName: {
            fontFamily: theme.fontFamily,
            fontSize: 11,
            color: colors.black[500],
        },
        photoSection: {
            marginBottom: spacing.xl,
        },
        photoGrid: {
            flexDirection: "row",
            flexWrap: "wrap",
            gap: spacing.sm,
        },
        photoLargeWrapper: {
            width: "100%",
            height: 180,
            overflow: "hidden",
            borderRadius: 8,
            marginBottom: spacing.sm,
        },
        photoMediumWrapper: {
            width: "48.5%",
            height: 100,
            overflow: "hidden",
            borderRadius: 6,
        },
        photoSmallWrapper: {
            width: "32%",
            height: 80,
            overflow: "hidden",
            borderRadius: 6,
        },
        photoLarge: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        photoSmall: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        photoMedium: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
        },
        sectionWithBorder: {
            marginBottom: spacing.lg,
            paddingBottom: spacing.lg,
            borderBottomWidth: 1,
            borderBottomColor: colors.black[100],
        },
        sectionTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 9,
            fontWeight: "bold",
            color: colors.black[400],
            textTransform: "uppercase",
            letterSpacing: 0.8,
            marginBottom: spacing.sm,
        },
        description: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.black[600],
            lineHeight: 1.6,
        },
        scheduleGrid: {
            flexDirection: "row",
            flexWrap: "wrap",
        },
        scheduleItem: {
            width: "50%",
            marginBottom: spacing.md,
        },
        scheduleLabel: {
            ...typography.label,
            marginBottom: 3,
        },
        scheduleValue: {
            fontFamily: theme.fontFamily,
            fontSize: 12,
            fontWeight: "bold",
            color: colors.black[800],
        },
        specialFieldsSection: {
            backgroundColor: colors.black[50],
            borderRadius: 8,
            padding: spacing.lg,
            marginBottom: spacing.lg,
        },
        specialFieldsTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            fontWeight: "bold",
            color: colors.black[700],
            marginBottom: spacing.md,
        },
        specialFieldRow: {
            flexDirection: "row",
            marginBottom: spacing.sm,
            paddingBottom: spacing.sm,
            borderBottomWidth: 1,
            borderBottomColor: colors.black[100],
        },
        specialFieldRowLast: {
            borderBottomWidth: 0,
            marginBottom: 0,
            paddingBottom: 0,
        },
        specialFieldLabel: {
            fontFamily: theme.fontFamily,
            fontSize: 9,
            fontWeight: "bold",
            color: colors.black[500],
            width: 120,
        },
        specialFieldValue: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.black[700],
            flex: 1,
        },
        additionalInfoSection: {
            backgroundColor: colors.black[50],
            borderRadius: 8,
            padding: spacing.lg,
            marginBottom: spacing.lg,
            borderLeftWidth: 3,
            borderLeftColor: colors.blue[500],
        },
        notesSection: {
            backgroundColor: colors.black[50],
            borderRadius: 8,
            padding: spacing.lg,
            marginBottom: spacing.lg,
            borderLeftWidth: 3,
            borderLeftColor: colors.orange[400],
        },
        notesText: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.black[700],
            lineHeight: 1.6,
        },
        pricingSection: {
            backgroundColor: colors.black[950],
            borderRadius: 8,
            padding: spacing.lg,
            marginTop: "auto",
        },
        pricingTitle: {
            fontFamily: theme.fontFamily,
            fontSize: 9,
            fontWeight: "bold",
            color: colors.black[300],
            textTransform: "uppercase",
            letterSpacing: 0.8,
            marginBottom: spacing.md,
        },
        pricingRow: {
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: spacing.sm,
        },
        pricingLabel: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.black[300],
        },
        pricingValue: {
            fontFamily: theme.fontFamily,
            fontSize: 10,
            color: colors.white,
        },
        pricingDivider: {
            height: 1,
            backgroundColor: colors.black[700],
            marginVertical: spacing.sm,
        },
        pricingTotalRow: {
            flexDirection: "row",
            justifyContent: "space-between",
            marginTop: spacing.sm,
        },
        pricingTotalLabel: {
            fontFamily: theme.fontFamily,
            fontSize: 12,
            fontWeight: "bold",
            color: colors.white,
        },
        pricingTotalValue: {
            fontFamily: theme.fontFamily,
            fontSize: 16,
            fontWeight: "bold",
            color: colors.orange[400],
        },
        footer: {
            position: "absolute",
            bottom: 25,
            left: 40,
            right: 40,
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: spacing.sm,
            borderTopWidth: 1,
            borderTopColor: colors.black[100],
        },
        footerText: {
            fontFamily: theme.fontFamily,
            fontSize: 8,
            color: colors.black[400],
        },
        pageIndicator: {
            fontFamily: theme.fontFamily,
            fontSize: 8,
            color: colors.black[400],
        },
    };
    return {
        theme,
        colors,
        spacing,
        typography,
        coverStyles,
        serviceStyles,
    };
}
export const { colors, spacing, typography } = createItineraryPdfStyles();
